import { ReactNode, useEffect, useRef, useState } from 'react'
import { AlertCircle } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useLoginRedirect } from '@/features/auth/api/loginRedirect'
import { useLoginCallback } from '@/features/auth/api/loginCallback'
import { paths } from '@/config/paths'
import { authAction } from '@/store/actions/authAction'
import { AppDispatch } from '@/store/store'
import { useDispatch } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { LoadingPage } from '@/pages/loading'

type AuthLoaderProps = {
  children: ReactNode
}

const AuthLoader = ({ children }: AuthLoaderProps) => {
  const [isAuthComleted, setIsAuthComleted] = useState<unknown>(null)
  const [authError, setAuthError] = useState<unknown>(null)

  //Login redirect
  const loginRedirectMutation = useLoginRedirect({
    mutationConfig: {
      onSuccess: () => {
        const redirectUrl = location.pathname + location.search
        sessionStorage.setItem('redirectUrl', redirectUrl !== paths.app.root.getHref() ? redirectUrl : paths.app.dashboard.getHref())
      },
      retry: true,
      retryDelay: 5000,
    },
  })

  //login callback
  const dispatch = useDispatch<AppDispatch>()
  const queryClient = useQueryClient()
  const loginCallbackMutation = useLoginCallback({
    mutationConfig: {
      onSuccess: async (res) => {
        const redirectUrl = sessionStorage.getItem('redirectUrl')
        sessionStorage.removeItem('redirectUrl')
        window.history.replaceState({}, '', redirectUrl)
        await dispatch(authAction({ authResponse: res, queryClient })).unwrap()
        setIsAuthComleted(true)
      },
      onError: (error) => setAuthError(error),
    },
  })

  const isCallbacked = useRef(false)
  useEffect(() => {
    if (isCallbacked.current === false) {
      const searchParams = new URLSearchParams(window.location.search)
      if (!searchParams.has('code')) {
        // login
        loginRedirectMutation.mutate()
      } else {
        // callback
        loginCallbackMutation.mutate({ code: searchParams.get('code')! })
      }
    }
    return () => {
      isCallbacked.current = true
    }
  }, [loginRedirectMutation, loginCallbackMutation])

  if (isAuthComleted) {
    return children
  }

  if (authError) {
    return (
      <div className="h-dvh flex flex-col items-center justify-center">
        <div className="flex flex-col items-center">
          <Alert variant="destructive" className="max-w-xl mb-2">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error occurred</AlertTitle>
            <AlertDescription>Something went wrong. Please try again later or contact support if the issue persists</AlertDescription>
          </Alert>
          <Button onClick={() => (window.location.href = paths.app.root.getHref())}>Reload</Button>
        </div>
      </div>
    )
  }

  return (
    <LoadingPage>
      <span className="text-muted-foreground font-medium mb-2">Connecting to the api service...</span>
    </LoadingPage>
  )
}

export { AuthLoader }
